/**
 * @fileoverview Day-One Documents data table.
 *
 * TanStack-table powered table for the generated precedents list. Adds
 * search, phase/contact filters, group-by (None / Contact / Contact type / Phase / Document),
 * row selection, and standard sorting on top of the existing day-one
 * precedents collection. Generated-precedent attachments nest under
 * the pack that attaches them (see `nestGeneratedAttachments`).
 *
 * Used by `routes/_app/_auth/dashboard/_layout.jobs.$jobId.day-one.tsx`.
 *
 * Group-by preference is persisted to `localStorage` under
 * `morgan:dayone:groupBy:<jobId>` so each job remembers its own
 * preferred grouping.
 *
 * Composition contract:
 *   - Pure rendering. The parent owns the precedents query, preview
 *     dialog state, and download/delete mutations; this component
 *     surfaces them via callbacks on `meta`.
 *   - The Contact column relies on `contactDisplayName` /
 *     `contactExists` being populated by `listByJob` — see
 *     `convex/jobs/precedents/queries.ts`.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getGroupedRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnFiltersState,
  type ExpandedState,
  type GroupingState,
  type RowSelectionState,
  type SortingState,
} from "@tanstack/react-table";
import {
  ChevronDown,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  Filter,
  Search,
  X,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import { Checkbox, Input } from "@/ui/primitives";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";
import { Button } from "@/ui/primitives";
import { cn } from "@/utils/misc";
import { type DayOneGrouping, dayOneColumns } from "./columns";
import type { DayOneTableMeta } from "./columns.meta";
import {
  CONTACT_FILTER_OPTIONS,
  PHASE_FILTER_OPTIONS,
  contactTypeGroupLabel,
  phaseGroupLabel,
} from "./columns.helpers";
import type { DayOnePrecedentRow } from "./types";
import {
  flattenDayOneRows,
  rowOrDescendantMatchesSearch,
} from "./nest-generated-attachments";

const STORAGE_KEY_PREFIX = "morgan:dayone:groupBy:";

interface DayOneDataTableProps {
  /** Job id used as the localStorage key for the group-by preference. */
  jobId: string;
  rows: DayOnePrecedentRow[];
  /** Currently-selected ids — controlled by the parent so the bulk
   *  actions toolbar stays in sync with row checkbox state. */
  selectedIds: Set<string>;
  onSelectedIdsChange: (next: Set<string>) => void;
  /** Side-channel callbacks dispatched from cell renderers. */
  meta: DayOneTableMeta;
  /** Optional row-click handler (defaults to opening preview via meta). */
  onRowClick?: (row: DayOnePrecedentRow) => void;
  /** Page-level actions rendered on the right of the filter toolbar so
   *  they share a row with search / filters instead of floating above. */
  toolbarActions?: ReactNode;
}

/** Multi-select filter dropdown — mirrors the precedent library
 *  `FilterDropdown` so the two surfaces feel identical. */
function FilterDropdown<V extends string>({
  label,
  options,
  selected,
  onToggle,
  onClear,
}: {
  label: string;
  options: ReadonlyArray<{ value: V; label: string }>;
  selected: ReadonlySet<V>;
  onToggle: (value: V) => void;
  onClear: () => void;
}) {
  const count = selected.size;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            "h-8 gap-1.5 text-xs",
            count > 0 && "border-primary/40 text-primary",
          )}
        >
          <Filter className="size-3" />
          {label}
          {count > 0 && (
            <span className="rounded-full bg-primary/10 px-1.5 text-[10px] font-semibold text-primary">
              {count}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-56 p-1"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="max-h-64 overflow-y-auto">
          {options.map((opt) => {
            const checked = selected.has(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => onToggle(opt.value)}
                className={cn(
                  "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-xs transition",
                  checked
                    ? "bg-primary/5 text-primary"
                    : "hover:bg-secondary/60",
                )}
              >
                <div
                  className={cn(
                    "flex size-3.5 shrink-0 items-center justify-center rounded-sm border",
                    checked
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border",
                  )}
                >
                  {checked && (
                    <span className="size-1.5 rounded-sm bg-primary-foreground" />
                  )}
                </div>
                <span className="truncate">{opt.label}</span>
              </button>
            );
          })}
        </div>
        {count > 0 && (
          <button
            type="button"
            onClick={onClear}
            className="mt-1 flex w-full items-center gap-1 border-t border-border px-2 py-1.5 text-[11px] text-primary/60 hover:text-primary"
          >
            <X className="size-3" />
            Clear {label.toLowerCase()}
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}

/** Group-by picker — reuses `FilterDropdown` styling but only allows
 *  a single value at a time. */
function GroupByPicker({
  value,
  onChange,
}: {
  value: DayOneGrouping;
  onChange: (next: DayOneGrouping) => void;
}) {
  const label =
    value === "none"
      ? "No grouping"
      : value === "contact"
        ? "Group: Contact"
        : value === "contactType"
          ? "Group: Contact type"
          : value === "phase"
            ? "Group: Phase"
            : "Group: Document";
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            "h-8 gap-1.5 text-xs",
            value !== "none" && "border-primary/40 text-primary",
          )}
        >
          {label}
          <ChevronDown className="size-3" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-44 p-1"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {(
          [
            "none",
            "contact",
            "contactType",
            "phase",
            "document",
          ] as DayOneGrouping[]
        ).map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            className={cn(
              "flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-left text-xs transition",
              opt === value
                ? "bg-primary/5 text-primary"
                : "hover:bg-secondary/60",
            )}
          >
            <span>
              {opt === "none"
                ? "None"
                : opt === "contact"
                  ? "Contact"
                  : opt === "contactType"
                    ? "Contact type"
                    : opt === "phase"
                      ? "Phase"
                      : "Document"}
            </span>
            {opt === value && <span className="text-primary">✓</span>}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

/** Format the parent row label for a grouped row — falls back to "(none)"
 *  for the special `__none__` bucket of un-fanned-out documents. */
function formatGroupLabel(grouping: DayOneGrouping, value: unknown): string {
  if (grouping === "phase") {
    return phaseGroupLabel(String(value ?? "__none__"));
  }
  if (grouping === "contactType") {
    return contactTypeGroupLabel(String(value ?? "__none__"));
  }
  if (grouping === "document") return String(value ?? "Untitled document");
  if (grouping === "contact") {
    const raw = String(value ?? "");
    if (!raw || raw === "__none__") return "No contact";
    // The contact column's `getGroupingValue` is `${kind}::${displayName}` —
    // strip the prefix for a cleaner header.
    const idx = raw.indexOf("::");
    return idx >= 0 ? raw.slice(idx + 2) : raw;
  }
  return String(value ?? "—");
}

/** Map the wizard-facing grouping value to the underlying TanStack
 *  column id. The Document column is keyed `name` internally. */
function groupingToColumnId(grouping: DayOneGrouping): string | null {
  if (grouping === "none") return null;
  if (grouping === "document") return "name";
  return grouping;
}

export function DayOneDataTable({
  jobId,
  rows,
  selectedIds,
  onSelectedIdsChange,
  meta,
  onRowClick,
  toolbarActions,
}: DayOneDataTableProps) {
  // ---- Local UI state ------------------------------------------------------
  const [searchText, setSearchText] = useState("");
  const [phaseFilters, setPhaseFilters] = useState<Set<string>>(
    () => new Set(),
  );
  const [contactFilters, setContactFilters] = useState<Set<string>>(
    () => new Set(),
  );
  const [sorting, setSorting] = useState<SortingState>([
    { id: "name", desc: false },
  ]);
  const [grouping, setGrouping] = useState<DayOneGrouping>(() => {
    if (typeof window === "undefined") return "none";
    const raw = window.localStorage.getItem(STORAGE_KEY_PREFIX + jobId);
    return raw === "contact" ||
      raw === "contactType" ||
      raw === "phase" ||
      raw === "document"
      ? raw
      : "none";
  });

  // `expanded: true` opens every group by default — without it,
  // `getRowModel()` returns only the parent header rows when grouping
  // is on, which is why expanding a group via click previously
  // showed an empty body. Reset whenever the grouping mode changes
  // so a freshly switched-on grouping starts fully open.
  const [expanded, setExpanded] = useState<ExpandedState>(true);
  useEffect(() => {
    setExpanded(true);
  }, [grouping]);

  // Persist group-by per job so re-opening day-one returns to the same view.
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_KEY_PREFIX + jobId, grouping);
  }, [grouping, jobId]);

  // ---- Derived: column filters / grouping state for TanStack ---------------
  const columnFilters = useMemo<ColumnFiltersState>(() => {
    const filters: ColumnFiltersState = [];
    if (phaseFilters.size > 0) {
      filters.push({ id: "phase", value: [...phaseFilters] });
    }
    if (contactFilters.size > 0) {
      filters.push({ id: "contact", value: [...contactFilters] });
    }
    return filters;
  }, [phaseFilters, contactFilters]);

  const groupingState = useMemo<GroupingState>(() => {
    const colId = groupingToColumnId(grouping);
    return colId ? [colId] : [];
  }, [grouping]);

  // While grouped by contact type, sort by the hidden column first so the
  // groups follow the canonical role/category order (banks together, "No
  // contact" last) instead of first-occurrence order.
  const sortingState = useMemo<SortingState>(
    () =>
      grouping === "contactType"
        ? [{ id: "contactType", desc: false }, ...sorting]
        : sorting,
    [grouping, sorting],
  );

  // ---- Row selection ↔ parent set bridge -----------------------------------
  const rowSelection = useMemo<RowSelectionState>(() => {
    const map: RowSelectionState = {};
    for (const row of flattenDayOneRows(rows)) {
      // File rows borrow their pack's id; they're never selectable.
      if (row.isAttachment) continue;
      if (selectedIds.has(row._id)) map[row.tableRowId] = true;
    }
    return map;
  }, [selectedIds, rows]);

  // ---- Global filter (search across name + contact display name) -----------
  const globalFilterFn = useCallback(
    (
      row: { original: DayOnePrecedentRow },
      _columnId: string,
      value: string,
    ) => {
      if (!value) return true;
      // Once a pack is visible, keep its attachments listed so the
      // practitioner still sees the full pack. Attachment-only rows are
      // never top-level, so this can't leak them into the root list.
      if (row.original.isAttachment) return true;
      return rowOrDescendantMatchesSearch(row.original, value);
    },
    [],
  );

  const allTableRows = useMemo(() => flattenDayOneRows(rows), [rows]);

  const table = useReactTable({
    data: rows,
    columns: dayOneColumns,
    state: {
      sorting: sortingState,
      columnFilters,
      grouping: groupingState,
      globalFilter: searchText,
      rowSelection,
      expanded,
      // The contactType column only feeds grouping/sorting — never shown.
      columnVisibility: { contactType: false },
    },
    // Annexure (attachment) rows are not independently selectable — they
    // always travel with their pack for downloads and emails, and letting
    // them join the selection made bulk downloads duplicate every annexure
    // (once merged into its pack, once standalone) — MOR-971.
    enableRowSelection: (row) => !row.original.isAttachment,
    enableGrouping: true,
    enableExpanding: true,
    onRowSelectionChange: (updater) => {
      const next =
        typeof updater === "function" ? updater(rowSelection) : updater;
      const nextSet = new Set<string>();
      const byTableRowId = new Map(
        allTableRows.map((r) => [r.tableRowId, r._id]),
      );
      for (const [id, selected] of Object.entries(next)) {
        if (!selected) continue;
        nextSet.add(byTableRowId.get(id) ?? id);
      }
      onSelectedIdsChange(nextSet);
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: (val) =>
      setSearchText(typeof val === "string" ? val : ""),
    onExpandedChange: setExpanded,
    globalFilterFn,
    getRowId: (row) => row.tableRowId,
    getSubRows: (row) => row.subRows,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    autoResetExpanded: false,
    autoResetPageIndex: false,
    meta,
  });

  const toggleSetMember = useCallback(
    <V extends string>(
      setter: React.Dispatch<React.SetStateAction<Set<V>>>,
      value: V,
    ) =>
      setter((prev) => {
        const next = new Set(prev);
        if (next.has(value)) next.delete(value);
        else next.add(value);
        return next;
      }),
    [],
  );

  const visibleRows = table.getRowModel().rows;
  const totalRows = rows.length;

  // Expand / collapse every pack's attachment rows at once (MOR-971).
  // Group header rows keep whatever state they already have so toggling
  // attachments doesn't fold the contact groups away too.
  const preExpandedRows = table.getPreExpandedRowModel().flatRows;
  const packRows = preExpandedRows.filter(
    (r) => !r.getIsGrouped() && r.subRows.length > 0,
  );
  const hasPacks = packRows.length > 0;
  const anyPackExpanded = packRows.some((r) => r.getIsExpanded());
  const groupRows = preExpandedRows.filter((r) => r.getIsGrouped());
  const hasGroups = groupRows.length > 0;
  const anyGroupExpanded = groupRows.some((r) => r.getIsExpanded());

  // Rebuild the explicit expanded map, overriding one kind of row (packs
  // or groups) and preserving the other kind's current state.
  const setExpandedByKind = (kind: "packs" | "groups", open: boolean) => {
    const next: Record<string, boolean> = {};
    for (const r of preExpandedRows) {
      const isGroup = r.getIsGrouped();
      if (!isGroup && r.subRows.length === 0) continue;
      const matches = kind === "groups" ? isGroup : !isGroup;
      const value = matches ? open : r.getIsExpanded();
      if (value) next[r.id] = true;
    }
    setExpanded(next);
  };
  const setAllPacksExpanded = (open: boolean) =>
    setExpandedByKind("packs", open);
  const setAllGroupsExpanded = (open: boolean) =>
    setExpandedByKind("groups", open);
  const filteredCount = table
    .getFilteredRowModel()
    .rows.filter((r) => !r.getIsGrouped()).length;

  return (
    <div className="space-y-3">
      {/* Filter / search toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-primary/40" />
          <Input
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search documents or contacts…"
            className="h-8 w-64 pl-8 text-xs"
          />
        </div>
        <FilterDropdown
          label="Phase"
          options={PHASE_FILTER_OPTIONS}
          selected={phaseFilters}
          onToggle={(v) => toggleSetMember(setPhaseFilters, v)}
          onClear={() => setPhaseFilters(new Set())}
        />
        <FilterDropdown
          label="Contact"
          options={CONTACT_FILTER_OPTIONS.map((o) => ({
            value: o.value as string,
            label: o.label,
          }))}
          selected={contactFilters}
          onToggle={(v) => toggleSetMember(setContactFilters, v)}
          onClear={() => setContactFilters(new Set())}
        />
        <GroupByPicker value={grouping} onChange={setGrouping} />
        {hasGroups && (
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 text-xs"
            onClick={() => setAllGroupsExpanded(!anyGroupExpanded)}
            title={
              anyGroupExpanded ? "Collapse every group" : "Expand every group"
            }
          >
            {anyGroupExpanded ? (
              <ChevronsDownUp className="size-3.5" />
            ) : (
              <ChevronsUpDown className="size-3.5" />
            )}
            {anyGroupExpanded ? "Collapse groups" : "Expand groups"}
          </Button>
        )}
        {hasPacks && (
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 text-xs"
            onClick={() => setAllPacksExpanded(!anyPackExpanded)}
            title={
              anyPackExpanded
                ? "Hide every pack's attachments"
                : "Show every pack's attachments"
            }
          >
            {anyPackExpanded ? (
              <ChevronsDownUp className="size-3.5" />
            ) : (
              <ChevronsUpDown className="size-3.5" />
            )}
            {anyPackExpanded ? "Collapse attachments" : "Expand attachments"}
          </Button>
        )}
        {filteredCount !== totalRows && (
          <span className="text-xs text-primary/50">
            Showing {filteredCount} of {totalRows}
          </span>
        )}
        {toolbarActions && (
          <div className="ml-auto flex items-center gap-2">
            {toolbarActions}
          </div>
        )}
      </div>

      <div className="overflow-hidden rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] transition-shadow duration-200 hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)]">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow
                key={headerGroup.id}
                className="border-b border-border bg-secondary/30 hover:bg-secondary/30"
              >
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={cn(
                      "h-10 px-4 text-xs font-medium text-primary/50",
                      header.id === "actions" && "text-right",
                      header.id === "select" && "w-10",
                    )}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {visibleRows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={dayOneColumns.length}
                  className="h-24 text-center text-sm text-primary/40"
                >
                  No documents match the current filters.
                </TableCell>
              </TableRow>
            ) : (
              visibleRows.map((row) => {
                if (row.getIsGrouped()) {
                  const groupCount = row
                    .getLeafRows()
                    .filter((r) => !r.original.isAttachment).length;
                  // Whole-group selection so a contact-type bucket (e.g. all
                  // banks) can be picked in one click for bulk generation.
                  // Annexure rows are excluded — they always ride with their
                  // pack, so selecting them duplicated files in bulk
                  // downloads (MOR-971).
                  const leafIds = row
                    .getLeafRows()
                    .filter(
                      (r) => !r.getIsGrouped() && !r.original.isAttachment,
                    )
                    .map((r) => r.id);
                  const allSelected =
                    leafIds.length > 0 &&
                    leafIds.every((id) => selectedIds.has(id));
                  const someSelected = leafIds.some((id) =>
                    selectedIds.has(id),
                  );
                  return (
                    <TableRow
                      key={row.id}
                      className="cursor-pointer border-b border-border bg-muted/40 hover:bg-muted/60"
                      onClick={() => row.toggleExpanded()}
                    >
                      <TableCell
                        colSpan={dayOneColumns.length}
                        className="px-4 py-2"
                      >
                        <div className="flex items-center gap-2">
                          <span onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={
                                allSelected
                                  ? true
                                  : someSelected
                                    ? "indeterminate"
                                    : false
                              }
                              onCheckedChange={(checked) => {
                                const next = new Set(selectedIds);
                                for (const id of leafIds) {
                                  if (checked === true) next.add(id);
                                  else next.delete(id);
                                }
                                onSelectedIdsChange(next);
                              }}
                              aria-label="Select all documents in group"
                            />
                          </span>
                          <ChevronRight
                            className={cn(
                              "size-3.5 text-primary/40 transition-transform",
                              row.getIsExpanded() && "rotate-90",
                            )}
                          />
                          <span className="text-xs font-medium text-primary/80">
                            {formatGroupLabel(grouping, row.groupingValue)}
                          </span>
                          <span className="text-[10px] text-primary/40">
                            ({groupCount} document
                            {groupCount === 1 ? "" : "s"})
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                }
                return (
                  <TableRow
                    key={row.id}
                    className={cn(
                      "group cursor-pointer border-b border-border transition last:border-0",
                      row.original.isAttachment &&
                        "border-l-[3px] border-l-muted-foreground/40 bg-muted/20 hover:bg-muted/40",
                      row.getIsSelected()
                        ? "bg-muted/50"
                        : !row.original.isAttachment && "hover:bg-muted/30",
                    )}
                    onClick={() =>
                      onRowClick
                        ? onRowClick(row.original)
                        : meta.onPreview(row.original)
                    }
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={cn(
                          "px-4 py-3 align-top",
                          cell.column.id === "actions" && "text-right",
                          cell.column.id === "select" && "w-10",
                        )}
                      >
                        {cell.getIsPlaceholder()
                          ? null
                          : flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// Re-export the types and meta interface so the page module only needs
// to import from this file (matches the convention used by other
// data-table modules in the codebase).
export type { DayOneTableMeta } from "./columns.meta";
export type { DayOnePrecedentRow };
